/**
 * The WebGL plumbing for the Morph Resolve loading effect — the Morph Resolve lab's shader, ported
 * verbatim to WebGL1, plus the uniform table and the three texture slots it samples. No React here:
 * `MorphCanvas` owns the clock, the phases and the rAF; this module only knows how to draw one frame.
 *
 * WebGL1 rather than the lab's WebGL2, because the canvas cards ship to whatever browser the deck is
 * opened in and GLSL ES 1.00 is the floor everything supports. The port is mechanical and total:
 * `#version 300 es` dropped, `texture()` → `texture2D()`, `out vec4 frag` → `gl_FragColor`, and the
 * lab's `gl_VertexID` fullscreen triangle (a WebGL2-only trick) becomes a 3-vertex attribute buffer.
 * The fragment shader's math and its comments are the lab's, untouched — if the two ever diverge, the
 * lab is the reference, not this file.
 *
 * The parts map is the one representational change: the lab painted part indices into an RGBA canvas
 * and sampled `.r`; here the delays arrive as a bare `Uint8Array` (see `parts.ts`) and upload as a
 * single-channel LUMINANCE texture, which samples `.r` identically at a quarter of the bytes.
 */

const VS = `
attribute vec2 aPos;
void main(){ gl_Position = vec4(aPos, 0., 1.); }`;

/*
 * The lab's fragment shader (Morph Resolve, lines ~635-850), WebGL1-ified as described above.
 * One pass over the whole card: the wait band, the dissolve and the resolve are the same pipeline —
 * the same blur, the same hard cutoff, the same warp — differing only in what is fed into it.
 */
const FS = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform sampler2D uFrom, uTo, uIdx;
uniform vec2  uRes;
uniform float uP, uBlur, uThr, uEdge, uWarp, uFreq, uTime;
uniform vec3  uGroundA, uGroundB;   // the card's own colour, which never melts
uniform float uHold;    // 1 while waiting: the content is held at full blur, wobbling
uniform float uGen;     // 1 when generating from nothing, so there is no outgoing state
uniform float uStagger; // how far apart the elements arrive
uniform vec3  uWaitCol; // the waiting band's colour, which owes nothing to the artifact
uniform float uGrain, uCellSize, uSites, uBandSpeed, uSeed, uWave;
uniform float uMulti, uSpread, uBaseHue;
uniform float uGrainScale, uCore, uSat;
uniform float uCover, uSweep, uSweepSpeed;

float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453123); }
float vnoise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  vec2 u = f*f*(3.0-2.0*f);
  return mix(mix(hash(i), hash(i+vec2(1,0)), u.x),
             mix(hash(i+vec2(0,1)), hash(i+vec2(1,1)), u.x), u.y);
}
float fbm(vec2 p){
  float v = 0.0, a = 0.5;
  for(int i=0;i<4;i++){ v += a*vnoise(p); p *= 2.03; a *= 0.5; }
  return v;
}

// golden-angle spiral: even coverage at any radius, no ring banding
vec4 blurTex(sampler2D t, vec2 uv, float r, vec2 px, float jitter){
  const int N = 64;
  vec4 sum = texture2D(t, uv);
  if(r < 0.35) return sum;
  float ga = 2.399963;
  for(int i=1;i<N;i++){
    float fi = float(i);
    float ang = fi*ga + jitter;
    float rad = sqrt(fi/float(N)) * r;
    sum += texture2D(t, uv + vec2(cos(ang), sin(ang)) * rad * px);
  }
  return sum / float(N);
}

/* The waiting state.
   Not one shape travelling about — several, in different places, on their own clocks.
   Each site fades up somewhere, holds, fades out, and the next cycle rerolls both its
   position and its size, so one appears left, another right, another in the middle, with
   delays between them and nothing ever quite repeating. Each is a dense core inside a
   soft aureole; the grain then eats only the outer edge, which is what turns a circle
   into something that reads as matter rather than as a shape. It owes the finished
   artifact nothing: while the model is still working, nobody knows its colours or its
   composition, so nothing here is derived from them. */
float h1(float n){ return fract(sin(n * 127.1) * 43758.5453); }

/* One body, not several. Its outline undulates — the radius is modulated around the
   angle at three frequencies, each drifting at its own rate, so it never settles into a
   circle and never repeats. Read it from above: a single mass with structure inside it,
   rather than separate things arriving one by one. */
float bodyField(vec2 p, vec2 asp, float T){
  vec2 c = vec2(0.5 * asp.x, 0.5)
         + vec2(0.06 * asp.x * sin(T * 0.19 + uSeed), 0.05 * cos(T * 0.23 + uSeed * 1.4));
  vec2 rad = vec2(0.40 * asp.x, 0.34) * uCover
           * (1.0 + 0.10 * sin(T * 0.27 + uSeed * 0.7));
  vec2 d = (p - c) / rad;
  float a = atan(d.y, d.x);
  float wob = 1.0
            + uWave * 0.34 * sin(a * 3.0 + T * 0.62 + uSeed)
            + uWave * 0.22 * sin(a * 5.0 - T * 0.47 + uSeed * 2.1)
            + uWave * 0.16 * sin(a * 2.0 + T * 0.29 + uSeed * 0.5)
            + uWave * 0.10 * sin(a * 8.0 + T * 0.71);
  // a superellipse rather than an ellipse: at uCover 1 it reaches the corners of the
  // card instead of leaving four white wedges no amount of scaling can fill
  float pw = mix(2.0, 4.2, clamp(uCover - 0.9, 0.0, 1.0));
  float r  = pow(pow(abs(d.x), pw) + pow(abs(d.y), pw), 1.0 / pw);
  return 1.0 - r / max(wob, 0.25);
}

/* Clusters inside the body: denser knots that come up somewhere, hold and fade, so the
   mass has internal life without ever breaking into separate objects. */
vec3 hsv(float h, float s, float v){
  vec3 k = abs(fract(h + vec3(0.0, 2.0/3.0, 1.0/3.0)) * 6.0 - 3.0);
  return v * mix(vec3(1.0), clamp(k - 1.0, 0.0, 1.0), s);
}

// rgb carries the colour weighted by each cluster's mass, a carries the mass itself, so
// where two clusters overlap their hues mix in proportion rather than one winning
vec4 cellField(vec2 p, vec2 asp, float T){
  float sum = 0.0;
  vec3  tint = vec3(0.0);
  for(int i = 0; i < 14; i++){
    if(float(i) >= uSites) break;
    float fi = float(i) + 1.0;
    float period = 2.4 + h1(fi * 3.7 + uSeed) * 3.6;
    float ph  = T / period + h1(fi * 9.1 + uSeed);
    float cyc = floor(ph);
    float f   = fract(ph);
    // up, hold, down — the hold is what stops it looking like a blinking light
    float life = smoothstep(0.0, 0.26, f) * (1.0 - smoothstep(0.58, 1.0, f));
    if(life <= 0.002) continue;
    vec2 c = vec2(0.20 + 0.60 * h1(fi * 17.3 + cyc * 5.1 + uSeed),
                  0.22 + 0.56 * h1(fi * 29.7 + cyc * 7.3 + uSeed * 2.0)) * vec2(asp.x, 1.0);
    float r = uCellSize * (0.50 + 1.05 * h1(fi * 41.9 + cyc * 3.3 + uSeed));
    vec2 d = p - c;
    float w = life * exp(-dot(d, d) / max(r * r, 1e-5));
    sum += w;
    // a hue per cluster, rerolled with its position, spread around the run's base hue
    float hu = uBaseHue + (h1(fi * 53.1 + cyc * 11.7 + uSeed) - 0.5) * uSpread;
    float sa = uSat * (0.55 + 0.45 * h1(fi * 61.3 + cyc * 2.9));
    tint += w * hsv(fract(hu), sa, 0.86 + 0.14 * h1(fi * 7.7 + cyc));
  }
  return vec4(tint, sum);
}

void main(){
  vec2 uv = gl_FragCoord.xy / uRes;
  uv.y = 1.0 - uv.y;
  vec2 px = 1.0 / uRes;

  // symmetric ease so the middle of the morph is where the fusion peaks
  float t = uP*uP*(3.0 - 2.0*uP);
  float mid = 1.0 - abs(t*2.0 - 1.0);      // 0 at both ends, 1 in the middle

  // One warp field over the whole card, so everything on it moves together. During the
  // hold the alpha gradient is spread over the full blur radius, so a warp sized for the
  // resolve tears the edges into filaments — it draws the noise instead of moving the
  // mass. So the hold gets a wider, gentler field plus a slow bodily drift, which reads
  // as the shape breathing rather than as fur.
  float amp  = max(mid, uHold * 0.30);
  float freq = mix(uFreq, uFreq * 0.34, uHold);
  vec2 wp = uv * freq;
  float n1 = fbm(wp + vec2(uTime*0.05, uTime*0.037));
  float n2 = fbm(wp + vec2(5.2, 1.3) - vec2(uTime*0.041, uTime*0.028));
  vec2 warp = (vec2(n1, n2) - 0.5) * uWarp * amp * px * 2.0;
  vec2 drift = vec2(sin(uTime * 0.47), cos(uTime * 0.31)) * uHold * 14.0 * px;
  vec2 suv = uv + warp + drift;

  float jitter = hash(gl_FragCoord.xy * 1.7) * 6.283;

  // Each element has its own clock. The index map says which one this pixel belongs to
  // and the elements are numbered in reading order, so the artifact arrives a piece at a
  // time — the same way it is streamed — instead of the whole card sharpening at once.
  float idx = texture2D(uIdx, uv).r;
  float lead = idx * uStagger;
  float local = clamp((t - lead) / max(1e-3, 1.0 - uStagger), 0.0, 1.0);
  // waiting means this element is present at full weight but nowhere near resolved: the
  // artifact's own content, blurred past reading and wobbling in place, which is the same
  // effect the resolve uses rather than a second one invented for the wait
  float sharp = mix(local, 0.0, uHold);

  // the outgoing state blurs away as the incoming one sharpens
  vec4 A = uGen > 0.5 ? vec4(0.0) : blurTex(uFrom, suv, uBlur * t, px, jitter);
  vec4 B = uHold > 0.995 ? vec4(0.0)
         : blurTex(uTo, suv, uBlur * (1.0 - sharp), px, jitter);

  // generating from nothing is not a crossfade — there is only one thing, sharpening.
  // So the incoming state carries full weight from the start and only its blur moves.
  float wB = max(local, uGen * (1.0 - uHold));
  vec4 m = A * (1.0 - local) * (1.0 - uGen) + B * wB;

  // a hard threshold the whole way — the blur carries the change, not a fade
  // the sites, and the grain that gives each one its dissolving aureole
  float sites = 0.0;
  vec3  waitCol = uWaitCol;
  if(uHold > 0.001){
    float T = uTime * uBandSpeed;
    vec2 asp = vec2(uRes.x / uRes.y, 1.0);
    vec2 buv = (uv + warp * 1.6) * asp;
    // the body carries the shape; the clusters only thicken it from the inside
    float bodyM = bodyField(buv, asp, T) * 1.35;
    vec4  cl    = cellField(buv, asp, T);
    float mass  = bodyM + cl.a * 0.40;
    // Two slow waves crossing in opposite directions. Their sum gathers density on one
    // side, carries it over and gathers it on the other, so the mass reads as moving
    // even where its outline is not.
    float S1 = sin(uv.x * 3.4 - T * uSweepSpeed * 0.62 + uSeed);
    float S2 = sin(uv.x * 1.7 + uv.y * 0.9 + T * uSweepSpeed * 0.37 + uSeed * 1.7);
    mass *= 1.0 + uSweep * (S1 * 0.62 + S2 * 0.38);
    // the body keeps the given colour; the clusters bring their own, in proportion
    vec3 clCol  = cl.a > 1e-4 ? cl.rgb / cl.a : uWaitCol;
    waitCol = mix(uWaitCol, clCol,
                  uMulti * clamp(cl.a * 0.40 / max(mass, 1e-4), 0.0, 1.0));
    // speckle, not streaks: three scales of isotropic grain, so the edge breaks up into
    // matter the way the reference does rather than into brush marks
    float gs = uGrainScale;
    float g = fbm(buv * 430.0 * gs + vec2(T * 0.29, -T * 0.19)) * 0.40
            + fbm(buv * 165.0 * gs - vec2(T * 0.15, T * 0.11))  * 0.31
            + fbm(buv *  62.0 * gs + vec2(T * 0.09, T * 0.06))  * 0.19
            + fbm(buv *  24.0 * gs - vec2(T * 0.05, T * 0.03))  * 0.10;
    mass += (g - 0.5) * uGrain * mix(1.0, uCore, smoothstep(0.40, 1.15, mass));
    sites = max(0.0, mass) * uHold;
  }

  // one mass, one cutoff. A wider band while the sites dominate, so the grain reads as a
  // dissolve rather than as speckle on a clean edge, narrowing as the artifact takes over.
  float total = m.a + sites;
  float edge = uEdge + uHold * 0.16;
  float alpha = smoothstep(uThr - edge, uThr + edge, total);
  vec3 ink = m.rgb / max(m.a, 1e-4);

  // land on the exact render: past 94% the real pixels take over, so type finishes
  // as type and a photograph finishes as a photograph
  vec4 rawB = texture2D(uTo, uv);
  float land = smoothstep(0.94, 1.0, sharp);
  alpha = mix(alpha, rawB.a, land);
  ink   = mix(ink, rawB.rgb / max(rawB.a, 1e-4), land);

  // the ground is the card, so it crossfades flat and stays a full rectangle — only the
  // content on top of it is ever thresholded into goo
  vec3 ground = mix(uGroundA, uGroundB, t);
  // the stirring is the card's own material, a shade of whatever the ground is
  // the colour follows whichever is carrying the mass at this pixel, so the handover is
  // a tint sliding from the waiting colour to the artifact's own ink
  vec3 col = mix(waitCol, ink, clamp(m.a / max(total, 1e-4), 0.0, 1.0));
  gl_FragColor = vec4(mix(ground, col, alpha), 1.0);
}`;

/**
 * The uniform lookup table, keyed by the lab's own short names (its `u` object) so a value read off a
 * lab slider maps to a `set1f` call by inspection: slider `freq` → `set1f('freq', …)` → `uFreq`.
 */
const SCALAR_UNIFORMS = {
  p: 'uP',
  hold: 'uHold',
  gen: 'uGen',
  time: 'uTime',
  seed: 'uSeed',
  blur: 'uBlur',
  thr: 'uThr',
  edge: 'uEdge',
  warp: 'uWarp',
  freq: 'uFreq',
  stagger: 'uStagger',
  grain: 'uGrain',
  grainScale: 'uGrainScale',
  core: 'uCore',
  sat: 'uSat',
  spread: 'uSpread',
  baseHue: 'uBaseHue',
  multi: 'uMulti',
  cover: 'uCover',
  sweep: 'uSweep',
  sweepSpeed: 'uSweepSpeed',
  wave: 'uWave',
  sites: 'uSites',
  cellSize: 'uCellSize',
  bandSpeed: 'uBandSpeed',
} as const;

const COLOR_UNIFORMS = {
  waitCol: 'uWaitCol',
  groundA: 'uGroundA',
  groundB: 'uGroundB',
} as const;

export type MorphScalarUniform = keyof typeof SCALAR_UNIFORMS;
export type MorphColorUniform = keyof typeof COLOR_UNIFORMS;

export interface MorphProgram {
  set1f(name: MorphScalarUniform, value: number): void;
  set3f(name: MorphColorUniform, rgb: readonly [number, number, number]): void;
  /** The outgoing content (unit 0). `null` → a transparent 1x1, i.e. "nothing was on the card". */
  uploadFrom(source: HTMLCanvasElement | ImageBitmap | null): void;
  /** The incoming content (unit 1). `null` → transparent — during the wait there is nothing yet. */
  uploadTo(source: HTMLCanvasElement | ImageBitmap | null): void;
  /**
   * The reading-order delays (unit 2), one byte per grid cell, row-major from the top — the layout
   * `parts.ts` produces. `null` → a single zero byte: every pixel on the first clock, no stagger.
   */
  uploadParts(delays: Uint8Array | null, w: number, h: number): void;
  /** One frame at the given backing-store size. Caller sets the uniforms first; this binds and draws. */
  draw(width: number, height: number): void;
  /** Deletes every GL object this program created. The context itself belongs to the caller. */
  dispose(): void;
}

/** `#rrggbb` → the 0..1 triple `set3f` wants. The lab's `rgb()` helper, typed. */
export function rgb01(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16) / 255,
    parseInt(hex.slice(3, 5), 16) / 255,
    parseInt(hex.slice(5, 7), 16) / 255,
  ];
}

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
  const shader = gl.createShader(type);
  // `createShader` returns null on a lost context — throw rather than limp, the caller falls back.
  if (!shader) throw new Error('morph: createShader returned null');
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    throw new Error(gl.getShaderInfoLog(shader) ?? 'morph: shader compile failed');
  }
  return shader;
}

const BLANK_RGBA = new Uint8Array(4); // transparent, so a missing texture contributes no mass
const BLANK_PART = new Uint8Array(1); // delay 0 everywhere: the whole card resolves on one clock

/**
 * Compiles the pipeline on the given context and returns the frame API. Throws on any compile/link
 * failure (including a context that is already lost) — the component catches and falls back to no
 * effect at all, which is the correct degradation: the artifact still appears, just without ceremony.
 */
export function createMorphProgram(gl: WebGLRenderingContext): MorphProgram {
  const program = gl.createProgram();
  if (!program) throw new Error('morph: createProgram returned null');
  const vs = compileShader(gl, gl.VERTEX_SHADER, VS);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, FS);
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    throw new Error(gl.getProgramInfoLog(program) ?? 'morph: program link failed');
  }
  // One program on a dedicated context, so `useProgram` once is enough — nothing else ever binds.
  gl.useProgram(program);

  // The fullscreen triangle. The lab used gl_VertexID and no buffer at all; WebGL1 has no vertex id,
  // so the same three clip-space corners arrive through an attribute instead.
  const quad = gl.createBuffer();
  if (!quad) throw new Error('morph: createBuffer returned null');
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(program, 'aPos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const scalarLoc = {} as Record<MorphScalarUniform, WebGLUniformLocation | null>;
  for (const key of Object.keys(SCALAR_UNIFORMS) as MorphScalarUniform[]) {
    scalarLoc[key] = gl.getUniformLocation(program, SCALAR_UNIFORMS[key]);
  }
  const colorLoc = {} as Record<MorphColorUniform, WebGLUniformLocation | null>;
  for (const key of Object.keys(COLOR_UNIFORMS) as MorphColorUniform[]) {
    colorLoc[key] = gl.getUniformLocation(program, COLOR_UNIFORMS[key]);
  }
  const uRes = gl.getUniformLocation(program, 'uRes');

  function newTex(): WebGLTexture {
    const tex = gl.createTexture();
    if (!tex) throw new Error('morph: createTexture returned null');
    gl.bindTexture(gl.TEXTURE_2D, tex);
    // LINEAR on the parts map too, exactly as the lab filtered its index canvas: the soft ramp
    // between two parts' delay values is what keeps neighbouring elements from snapping apart.
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }
  const texFrom = newTex();
  const texTo = newTex();
  const texIdx = newTex();

  // Sampler units are fixed for the program's whole life, as in the lab: from=0, to=1, idx=2.
  gl.uniform1i(gl.getUniformLocation(program, 'uFrom'), 0);
  gl.uniform1i(gl.getUniformLocation(program, 'uTo'), 1);
  gl.uniform1i(gl.getUniformLocation(program, 'uIdx'), 2);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // premultiplied output

  function uploadRGBA(tex: WebGLTexture, source: HTMLCanvasElement | ImageBitmap | null): void {
    gl.bindTexture(gl.TEXTURE_2D, tex);
    // Premultiplied on the way in because the blend function above assumes it — the same global the
    // lab set once; here it is per-upload so the parts map below can opt out without ordering rules.
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    if (source) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    } else {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, BLANK_RGBA);
    }
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  }

  return {
    set1f(name, value) {
      gl.uniform1f(scalarLoc[name], value);
    },
    set3f(name, rgb) {
      gl.uniform3f(colorLoc[name], rgb[0], rgb[1], rgb[2]);
    },
    uploadFrom(source) {
      uploadRGBA(texFrom, source);
    },
    uploadTo(source) {
      uploadRGBA(texTo, source);
    },
    uploadParts(delays, w, h) {
      gl.bindTexture(gl.TEXTURE_2D, texIdx);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      // Row length is the grid width, not a multiple of 4 bytes — without this, any grid whose width
      // isn't divisible by 4 uploads sheared.
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      if (delays && delays.length === w * h) {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, w, h, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, delays);
      } else {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, 1, 1, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, BLANK_PART);
      }
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    },
    draw(width, height) {
      gl.viewport(0, 0, width, height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(uRes, width, height);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texFrom);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, texTo);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, texIdx);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    },
    dispose() {
      gl.deleteTexture(texFrom);
      gl.deleteTexture(texTo);
      gl.deleteTexture(texIdx);
      gl.deleteBuffer(quad);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      gl.deleteProgram(program);
    },
  };
}
